import { useQueryClient } from "@tanstack/react-query";
import { ReactNode } from "react";
import { useTranslation } from "react-i18next";

import { translateNestingError } from "#app/i18n/translateApiError";
import { colors } from "#app/theme/v2/colors";
import { Loader } from "#components/atoms/Loader/Loader";
import { Text } from "#components/atoms/Typographies/Text";
import { NestingItem } from "#modules/layoutsV2/view/NestingItemsTable";
import { NestingPlanCard } from "#modules/layoutsV2/view/NestingPlanCard";
import { NestingPlanEmpty } from "#modules/layoutsV2/view/NestingPlanEmpty";
import { NestingPlanView } from "#modules/layoutsV2/view/NestingPlanView";
import { queryKeys } from "#shared/queryKeys/queryKeys";
import { Flex } from "#shared/view/components/Flex";
import { JobStatusSchema, NestingRunView } from "shared";

type Props = {
  items: NestingItem[];
  nestingRun: NestingRunView | null;
  isLoading: boolean;
  billedShare: string;
  onBilledShareChange: (value: string) => void;
  onBilledShareBlur: () => void;
  isMaterialCostLoading: boolean;
  renderRowAction?: (itemId: string) => ReactNode;
};

export const NestingPlan = ({
  items,
  nestingRun,
  isLoading,
  billedShare,
  onBilledShareChange,
  onBilledShareBlur,
  isMaterialCostLoading,
  renderRowAction,
}: Props) => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const layouts = nestingRun?.layouts ?? [];
  const totalSheets = nestingRun?.sheetsUsed ?? layouts.reduce((sum, { count }) => sum + count, 0);
  const boundingBoxItemCount = nestingRun ? nestingRun.boundingBoxFlatPatternItemIds.length : 0;

  if (isLoading) {
    return (
      <NestingPlanCard isFilling>
        <Flex flex={1} align="center" justify="center">
          <Loader size="large" />
        </Flex>
      </NestingPlanCard>
    );
  }

  if (nestingRun?.status === JobStatusSchema.enum.ERROR) {
    const failedItemName = items.find(({ id }) => id === nestingRun.errorItemId)?.part.name;
    return (
      <NestingPlanCard>
        <Text size="small" color={colors.text.errorDefault}>
          {[
            t("layout.trueShapeNesting.failed"),
            translateNestingError(nestingRun, { name: failedItemName }),
          ].join(" : ")}
        </Text>
      </NestingPlanCard>
    );
  }

  if (layouts.length === 0) {
    return (
      <NestingPlanCard>
        <NestingPlanEmpty />
      </NestingPlanCard>
    );
  }

  return (
    <NestingPlanCard>
      {boundingBoxItemCount > 0 && (
        <Text size="small" color={colors.text.warning}>
          {t("layout.sidePanel.boundingBoxWarning", { count: boundingBoxItemCount })}
        </Text>
      )}
      <NestingPlanView
        items={items}
        layouts={layouts}
        totalSheets={totalSheets}
        billedShare={billedShare}
        onBilledShareChange={onBilledShareChange}
        onBilledShareBlur={onBilledShareBlur}
        isMaterialCostLoading={isMaterialCostLoading}
        renderRowAction={renderRowAction}
        onSvgUrlExpired={() =>
          void queryClient.invalidateQueries({
            queryKey: queryKeys.layoutNestings.detail(nestingRun?.layoutId),
          })
        }
      />
    </NestingPlanCard>
  );
};
